import { useState } from "react";
import { T } from "@/components/T";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { resolveNav, PATRIKA, PANCHANGA, ABOUT_LINKS, ACCOUNT_LINKS } from "@/lib/nav";
import { useSettings } from "@/context/SettingsContext";
import LanguageSelector from "@/components/LanguageSelector";
import ThemeToggle from "@/components/ThemeToggle";
import SearchOverlay from "@/components/SearchOverlay";
import NotificationBell from "@/components/NotificationBell";
import { setMenuOpen } from "@/lib/menuBus";
import { Search, Menu, Bookmark, ChevronDown, User, CalendarCheck, LogOut, LayoutDashboard, ArrowRight, Crown } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const triggerCls = "flex items-center gap-1 whitespace-nowrap font-nav-category text-[17px] text-ink-2 transition-colors hover:text-saffron focus:outline-none data-[state=open]:text-saffron";
const navLinkCls = ({ isActive }) => `whitespace-nowrap font-nav text-[17px] transition-colors hover:text-saffron ${isActive ? "text-saffron" : "text-ink-2"}`;

const SoonBadge = ({ en }) => (
  <span className="ml-auto shrink-0 rounded-sm border border-gold/40 px-1.5 py-0.5 text-[9px] uppercase tracking-[0.14em] text-gold">
    {en ? "Coming Soon" : "जल्द"}
  </span>
);

// A single primary category → clean editorial dropdown of its items.
function CategoryMenu({ group }) {
  const { pick } = useLang();
  const navigate = useNavigate();
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger data-testid={`nav-${group.key}`} className={triggerCls}>
        {pick(group.label.deva, group.label.en)} <ChevronDown className="h-3.5 w-3.5" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" sideOffset={16} className="w-[19rem] rounded-none border-line bg-surface p-0">
        <div className="border-b border-line px-4 py-3">
          <p className="overline-label text-ink-3">{pick(group.label.deva, group.label.en)}</p>
          <p className="mt-0.5 text-xs text-ink-3/80">{pick(group.blurb.deva, group.blurb.en)}</p>
        </div>
        <ul className="p-1.5">
          {group.items.map((l) => (
            <DropdownMenuItem
              key={l.to}
              onClick={() => navigate(l.to)}
              data-testid={`nav-link-${l.to.replace(/\//g, "")}`}
              className="group/item cursor-pointer gap-2.5 rounded-none px-2.5 py-2.5 font-nav text-[15px] text-ink-2 focus:bg-surface-alt focus:text-ink"
            >
              <span className="w-5 shrink-0 font-deva text-sm text-saffron/70">{l.deva?.[0]}</span>
              <span className="flex min-w-0 flex-col">
                <span className="truncate">{pick(l.deva, l.en)}</span>
                {l.sub && <span className="text-[11px] text-ink-3/80">{pick(l.sub.deva, l.sub.en)}</span>}
              </span>
              {l.soon
                ? <SoonBadge en />
                : <ArrowRight className="ml-auto h-3.5 w-3.5 shrink-0 opacity-0 transition-opacity group-hover/item:opacity-60" />}
            </DropdownMenuItem>
          ))}
        </ul>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function AccountMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  if (!user) {
    return (
      <Link to="/login" data-testid="header-signin" className="inline-flex items-center gap-1.5 border border-line px-3.5 py-1.5 text-sm text-ink-2 transition-colors hover:border-[var(--ink)] hover:text-ink">
        <User className="h-4 w-4" /> <T>Sign in</T>
      </Link>
    );
  }
  const initial = (user.name || user.email || "?")[0].toUpperCase();
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger data-testid="account-menu" className="flex items-center gap-2 focus:outline-none">
        {user.picture ? <img src={user.picture} alt="" className="h-8 w-8 rounded-full object-cover" /> : <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--surface-alt)] text-sm text-saffron">{initial}</span>}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[13rem] rounded-none border-line bg-surface p-1">
        <DropdownMenuItem onClick={() => navigate("/panchanga-premium")} data-testid="account-premium-link" className="cursor-pointer font-nav font-medium text-saffron focus:bg-surface-alt focus:text-saffron"><Crown className="mr-2 h-4 w-4" /> <T>Panchangam Premium</T></DropdownMenuItem>
        <DropdownMenuSeparator className="bg-line" />
        <DropdownMenuItem onClick={() => navigate("/account")} className="cursor-pointer font-nav text-ink-2 focus:bg-surface-alt focus:text-ink"><User className="mr-2 h-4 w-4" /> <T>Account</T></DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/my-bookings")} className="cursor-pointer font-nav text-ink-2 focus:bg-surface-alt focus:text-ink"><CalendarCheck className="mr-2 h-4 w-4" /> <T>My Bookings</T></DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/bookmarks")} className="cursor-pointer font-nav text-ink-2 focus:bg-surface-alt focus:text-ink"><Bookmark className="mr-2 h-4 w-4" /> <T>Saved</T></DropdownMenuItem>
        {user.role === "admin" && <DropdownMenuItem onClick={() => navigate("/admin")} className="cursor-pointer font-nav text-ink-2 focus:bg-surface-alt focus:text-ink"><LayoutDashboard className="mr-2 h-4 w-4" /> <T>Admin</T></DropdownMenuItem>}
        <DropdownMenuSeparator className="bg-line" />
        <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} className="cursor-pointer font-nav text-ink-2 focus:bg-surface-alt focus:text-ink"><LogOut className="mr-2 h-4 w-4" /> <T>Sign out</T></DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Header() {
  const { t, pick } = useLang();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { isVisible, nav_config, hub_sections, show_language_switcher } = useSettings();
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState(null);

  const DEFAULT_HUB_KEYS = new Set(["scriptures", "rishis", "concepts", "pilgrimage", "samskara", "sanskrit"]);
  const customByGroup = {};
  (hub_sections || []).filter((s) => s && s.key && !DEFAULT_HUB_KEYS.has(s.key)).forEach((s) => {
    const grp = s.group || "explore";
    if (!customByGroup[grp]) customByGroup[grp] = [];
    customByGroup[grp].push({ to: `/hub/${s.key}`, deva: s.label || s.key, en: s.label || s.key });
  });
  const groups = resolveNav(nav_config?.header)
    .map((g) => ({ ...g, items: g.items.filter((l) => (l.to === "/explore" || isVisible(l.to)) && !l.hideNav) }))
    .map((g) => (customByGroup[g.key] ? { ...g, items: [...g.items, ...customByGroup[g.key]] } : g))
    .filter((g) => g.items.length > 0);

  const mobileGroup = (group) => {
    const isOpen = openGroup === group.key;
    return (
      <div key={group.key} className="border-b border-line">
        <button type="button" onClick={() => setOpenGroup(isOpen ? null : group.key)}
          className="flex w-full cursor-pointer list-none items-center justify-between py-3.5 text-ink" data-testid={`mnav-${group.key}`} aria-expanded={isOpen}>
          <span className="font-nav-category text-xl font-semibold tracking-normal text-ink deva-lh">{pick(group.label.deva, group.label.en)}</span>
          <ChevronDown className={`h-4 w-4 text-ink-3 transition-transform ${isOpen ? "rotate-180" : ""}`} />
        </button>
        {isOpen && (
          <div className="pb-2 pl-1">
            {group.items.map((it) => (
              <NavLink key={it.to} to={it.to} onClick={() => { setMobileOpen(false); setOpenGroup(null); }} className="flex items-center gap-2 py-2.5 font-nav text-[16px] text-ink-2 hover:text-saffron">
                <span className="min-w-0 flex-1 truncate">{pick(it.deva, it.en)}</span>
                {it.soon && <SoonBadge en />}
              </NavLink>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <header className="sticky top-0 z-40 border-b border-line bg-[var(--surface)]">
        <div className="mx-auto flex h-[68px] max-w-7xl items-center justify-between px-5 md:px-8">
          <Link to="/" data-testid="brand-logo" className="flex shrink-0 items-baseline gap-2 whitespace-nowrap leading-none">
            <span className="font-brand text-2xl text-ink md:text-[28px]">धर्मार्थ</span>
            <span className="hidden text-[10px] uppercase tracking-[0.28em] text-gold sm:inline"><T>Dharmartha</T></span>
          </Link>

          <div className="flex shrink-0 items-center gap-3 md:gap-4">
            <button data-testid="search-open-button" onClick={() => setSearchOpen(true)} aria-label="Search" className="text-ink-2 transition-colors hover:text-saffron"><Search className="h-5 w-5" /></button>
            <Link to="/bookmarks" data-testid="header-bookmarks-link" aria-label={t("bookmarks")} className="hidden text-ink-2 transition-colors hover:text-saffron sm:block"><Bookmark className="h-5 w-5" /></Link>
            {user && <NotificationBell />}
            <ThemeToggle />
            <Link to="/panchanga-premium" data-testid="header-premium-link" className="hidden items-center gap-1.5 rounded-full border border-[var(--gold-muted)] bg-[rgba(199,80,0,0.07)] px-3 py-1.5 text-sm font-medium text-saffron transition-colors hover:bg-saffron hover:text-white lg:inline-flex">
              <Crown className="h-4 w-4" /> <T>Premium</T>
            </Link>
            <div className="md:hidden text-ink-2" data-testid="mobile-language-selector"><LanguageSelector compact /></div>
            <div className="hidden md:block"><LanguageSelector /></div>
            <div className="hidden md:block"><AccountMenu /></div>

            <Sheet open={mobileOpen} onOpenChange={(v) => { setMobileOpen(v); setMenuOpen(v); }}>
              <SheetTrigger asChild>
                <button data-testid="mobile-menu-button" aria-label="Menu" className="inline-flex items-center gap-1.5 text-ink-2 transition-colors hover:text-saffron">
                  <Menu className="h-6 w-6" />
                  <span className="hidden text-[15px] font-nav sm:inline">{pick("मेनू", "Menu")}</span>
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="flex w-[86%] max-w-sm flex-col overflow-y-auto overflow-x-hidden border-line bg-surface">
                <SheetTitle className="sr-only">{pick("मेनू", "Menu")}</SheetTitle>
                <Link to="/" data-testid="mobile-brand-logo" onClick={() => setMobileOpen(false)} className="mb-5 mt-1 flex items-baseline gap-2">
                  <span className="font-brand text-3xl text-ink">धर्मार्थ</span>
                  <span className="text-[10px] uppercase tracking-[0.28em] text-gold"><T>Dharmartha</T></span>
                </Link>

                {groups.map((g) => mobileGroup(g))}

                <NavLink to="/panchanga-premium" onClick={() => setMobileOpen(false)} data-testid="mnav-panchang-premium" className="flex items-center justify-between border-b border-line py-3.5 font-nav text-[17px] text-saffron">
                  <span className="inline-flex items-center gap-2"><Crown className="h-4 w-4" /> {pick("पंचांग प्रीमियम", "Panchangam Premium")}</span>
                  <ArrowRight className="h-4 w-4" />
                </NavLink>

                {isVisible(PANCHANGA.to) && (
                  <NavLink to={PANCHANGA.to} onClick={() => setMobileOpen(false)} className="flex items-center justify-between border-b border-line py-3.5 font-nav text-[17px] text-ink" data-testid="mnav-panchang">
                    <span>{pick(PANCHANGA.deva, PANCHANGA.en)}</span>
                    <span className="text-[11px] text-ink-3/80">{pick(PANCHANGA.sub.deva, PANCHANGA.sub.en)}</span>
                  </NavLink>
                )}

                <NavLink to={PATRIKA.to} onClick={() => setMobileOpen(false)} className="flex items-center justify-between border-b border-line py-3.5 font-nav text-[17px] text-ink">
                  <span>{pick(PATRIKA.deva, PATRIKA.en)}</span>
                  <span className="text-[11px] text-ink-3/80">{pick(PATRIKA.sub.deva, PATRIKA.sub.en)}</span>
                </NavLink>

                {user ? (
                  <div className="border-b border-line py-2">
                    {ACCOUNT_LINKS.map((it) => (
                      <NavLink key={it.to} to={it.to} onClick={() => setMobileOpen(false)} className="block py-2.5 font-nav text-[17px] text-ink hover:text-saffron">{pick(it.deva, it.en)}</NavLink>
                    ))}
                    <button type="button" data-testid="mobile-signout-button"
                      onClick={async () => { setMobileOpen(false); await logout(); navigate("/"); }}
                      className="mt-1 flex w-full items-center gap-2 py-2.5 text-left font-nav text-[17px] text-saffron hover:text-[var(--saffron-dark)]">
                      <LogOut className="h-4 w-4" /> {pick("साइन आउट", "Sign out")}
                    </button>
                  </div>
                ) : (
                  <NavLink to="/login" onClick={() => setMobileOpen(false)} className="block border-b border-line py-3.5 font-nav text-[17px] text-saffron"><T>Sign in</T></NavLink>
                )}

                {show_language_switcher && <div className="mt-auto pt-5"><LanguageSelector /></div>}
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>
      <SearchOverlay open={searchOpen} onOpenChange={setSearchOpen} />
    </>
  );
}
